/**
 * Targeted runtime polyfills for ECMAScript features that aren't
 * available in the browsers we still support (Safari 15.x).
 *
 * These helpers intentionally avoid pulling in `core-js/stable`
 * so that we only pay for the features we actively use.
 */

const defineValue = (target: object, key: PropertyKey, value: (...args: unknown[]) => unknown) => {
  Object.defineProperty(target, key, {
    configurable: true,
    writable: true,
    enumerable: false,
    value
  });
};

if (typeof Promise.withResolvers !== 'function') {
  defineValue(Promise, 'withResolvers', function withResolvers<T>(): PromiseWithResolvers<T> {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((promiseResolve, promiseReject) => {
      resolve = promiseResolve;
      reject = promiseReject;
    });

    return { promise, reject, resolve };
  });
}

if (typeof Array.prototype.toSorted !== 'function') {
  defineValue(Array.prototype, 'toSorted', function toSorted<
    T
  >(this: T[], compareFn?: (a: T, b: T) => number): T[] {
    const clone = Array.from(this);
    return clone.sort(compareFn);
  });
}

if (typeof Array.prototype.toSpliced !== 'function') {
  defineValue(Array.prototype, 'toSpliced', function toSpliced<
    T
  >(this: T[], start: number, deleteCount?: number, ...items: T[]): T[] {
    const clone = Array.from(this);
    const normalizedDeleteCount = deleteCount === undefined ? clone.length - start : deleteCount;
    clone.splice(start, normalizedDeleteCount, ...items);
    return clone;
  });
}

if (typeof Array.prototype.findLastIndex !== 'function') {
  defineValue(Array.prototype, 'findLastIndex', function findLastIndex<
    T
  >(this: T[], predicate: (value: T, index: number, array: T[]) => boolean, thisArg?: unknown): number {
    if (typeof predicate !== 'function') {
      throw new TypeError('Array.prototype.findLastIndex requires a predicate function');
    }

    for (let index = this.length - 1; index >= 0; index -= 1) {
      if (predicate.call(thisArg, this[index], index, this)) {
        return index;
      }
    }

    return -1;
  });
}

type GroupDictionary = Record<string, unknown[]> & { [key: symbol]: unknown[] };

const getGroup = (store: GroupDictionary, key: PropertyKey): unknown[] => {
  if (typeof key === 'symbol') {
    store[key] = store[key] ?? [];
    return store[key];
  }

  const stringKey = String(key);
  store[stringKey] = store[stringKey] ?? [];
  return store[stringKey];
};

if (typeof Object.groupBy !== 'function') {
  defineValue(Object, 'groupBy', function groupBy<
    T,
    K extends PropertyKey
  >(items: Iterable<T>, callbackfn: (value: T, index: number) => K): Record<K, T[]> {
    if (typeof callbackfn !== 'function') {
      throw new TypeError('Object.groupBy requires a callback function');
    }

    const store: GroupDictionary = Object.create(null);
    let index = 0;

    for (const item of items) {
      const bucket = getGroup(store, callbackfn(item, index));
      index += 1;
      bucket.push(item);
    }

    return store as Record<K, T[]>;
  });
}

if (typeof Map.groupBy !== 'function') {
  defineValue(Map, 'groupBy', function groupBy<
    T,
    K
  >(items: Iterable<T>, callbackfn: (value: T, index: number) => K): Map<K, T[]> {
    if (typeof callbackfn !== 'function') {
      throw new TypeError('Map.groupBy requires a callback function');
    }

    const map = new Map<K, T[]>();
    let index = 0;

    for (const item of items) {
      const key = callbackfn(item, index);
      index += 1;
      const bucket = map.get(key);

      if (bucket) {
        bucket.push(item);
      } else {
        map.set(key, [item]);
      }
    }

    return map;
  });
}
